import { FunctionComponent, useState, useEffect } from "react";
import { connect } from "react-redux";
import Chart from "react-apexcharts";

/** Props Definition */
type props = {
  analyticsData: any;
  type: "not_int_reason" | "lost_reason" | "call_back_reason";
  color: string[];
  style?: any;
  role: any;
  history: any;
  filter: "MTD" | "YTD" | "PM" | "All" | "CUSTOM";
  contacts: any;
  user: any;
  organizationUsers: any[];
  teamLeadUsers: any;
  branchUsers: any;
  theme: any; // true = light mode, false = dark mode
};

/**
 * DoughnutChartOrg
 *
 * This component:
 * ✔ Aggregates analytics data across organization/team/branch users.
 * ✔ Normalizes labels (e.g., "Invalid Number" → "Number Invalid").
 * ✔ Creates series + labels arrays dynamically.
 * ✔ Renders donut charts using ApexCharts.
 * ✔ Auto-adjusts text colors based on theme.
 */
const DoughnutChartOrg: FunctionComponent<props> = ({
  analyticsData,
  type,
  color,
  style,
  role,
  history,
  filter,
  contacts,
  user,
  organizationUsers,
  teamLeadUsers,
  branchUsers,
  theme
}) => {
  const [data, setData] = useState<any>({});
  const [allContacts, setAllContacts] = useState<any[] | undefined>(undefined);
  const [labels, setLabels] = useState<any>([]);
  const [series, setSeries] = useState<any>([]);

  /** -------------------------
   *  1. Flatten All Contacts
   * -------------------------- */
  useEffect(() => {
    let contactList: any[] = [];

    Object.keys(contacts.data).forEach((key) => {
      contactList = [...contactList, ...contacts.data[key]];
    });

    setAllContacts(contactList);
  }, [contacts.data]);

  /** --------------------------------------------
 *  2. Aggregate Analytics Data Across Users
 * --------------------------------------------*/
  useEffect(() => {
    const temp: { [key: string]: number } = {};
    Object.values(analyticsData).forEach((analytics: any) => {
      Object.keys(analytics.leadAnalytics[type]).forEach((key) => {
        if (temp[key]) {
          temp[key] += analytics.leadAnalytics[type][key];
        } else {
          temp[key] = analytics.leadAnalytics[type][key];
        }
      });
    });
    setData(temp);
  }, [analyticsData]);


  /** ------------------------
  *  3. Prepare Chart Labels
  * ------------------------*/
  useEffect(() => {
    const lablesArray: any = [];
    let labels = Object.keys(data);
    let series = Object.values(data);
    labels.forEach((key) => {
      if (key === "Invalid Number") {
        lablesArray.push("Number Invalid");
      } else {
        lablesArray.push(key);
      }
    })
    setLabels(lablesArray);
    setSeries(series);
  }, [data]);

  /** -----------------------------------------
  *  ApexCharts: Donut Chart (Used Everywhere)
  * ------------------------------------------*/
  const donutChartOptions: any = {
    series: series,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: any) {
          return val.toFixed(0) + "%"
        },
      },
      labels: labels,
      colors: [
        "#71CACC",
        "#87CBAC",
        "#3B6064",
        "#F88377",
        "#272944",
        "#aaf0d1",
        "#ff6961",
        "#93FCF8",
        "#00C9D2",
      ],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        labels: {
          colors: theme ? "#000" : "#fff",
        }
      },
      plotOptions: {
        pie: {
          customScale: 1,
          donut: {
            size: "60%",
          },
          offsetY: 20,
        },
        stroke: {
          colors: undefined,
        },
      }
    },
  };

  return (
    <>
      {type === "lost_reason" || type === "call_back_reason" ? (
        <Chart
          options={donutChartOptions.options}
          series={donutChartOptions.series}
          type="donut"
          width={"100%"}
          height={280}
        />
      ) : (
        <Chart
          options={donutChartOptions.options}
          series={donutChartOptions.series}
          type="donut"
          width={"100%"}
          height={280}
        />
      )}
    </>
  );
};

/** Map Redux State */
const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    contacts: state.contacts,
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    teamLeadUsers: state.teamLeadUsers.data,
    branchUsers: state.branchUsers.data,
    theme: state.theme.isLightMode
  };
};
export default connect(mapStateToProps)(DoughnutChartOrg);
